import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../../lib/adminApi";
import { TInput, Sel, Toggle, Btn, Card, Badge } from "../ui";
import ProductMultiPicker from "../ProductMultiPicker";
import { money } from "../../../lib/api";

const NEW_PROMO = {
  kind: "CODE", code: "", campaignName: "", active: false, discountType: "PERCENT", value: 10,
  startsAt: "", endsAt: "", minPurchase: null, maxTotalUses: null, maxUsesPerCustomer: null,
  newCustomersOnly: false, stackable: false, applicableProductIds: [], applicableCategoryIds: [],
  excludedProductIds: [], excludedCategoryIds: [], eligibleEmails: [], internalNotes: "",
};

function CategoryToggles({ label, value = [], onChange, categories, testid }) {
  return (
    <div className="md:col-span-2">
      <p className="font-mono text-[9px] tracking-[0.25em] text-white/45 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {categories.map((c) => (
          <button key={c.id}
            onClick={() => onChange(value.includes(c.id) ? value.filter((x) => x !== c.id) : [...value, c.id])}
            className={`font-mono text-[8px] tracking-[0.15em] border px-3 py-1.5 transition-colors ${
              value.includes(c.id) ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/40 hover:border-white/40"}`}>
            {c.name.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DiscountsTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState(null);
  const refresh = () => adm.promotions().then(setItems).catch(() => {});
  useEffect(() => { refresh(); adm.list("categories").then(setCategories).catch(() => {}); }, []);

  const set = (k, v) => setEditing((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const { _id, id, usesCount, createdAt, updatedAt, ...body } = editing;
      if (id) await adm.updatePromotion(id, body);
      else await adm.createPromotion(body);
      toast.success("Promotion saved.");
      setEditing(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const destroy = async (p) => {
    if (!window.confirm(`Delete promotion "${p.code || p.campaignName}"? Redemption history is preserved.`)) return;
    try { await adm.deletePromotion(p.id); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const showStats = async (p) => {
    try { setStats(await adm.promotionStats(p.id)); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="store-discounts-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 max-w-2xl">
          PROMO CODES + (LATER) AUTOMATIC PROMOTIONS. NON-STACKABLE BY DEFAULT. DISCOUNTS NEVER BYPASS DIMENSION V QUALIFICATION.
        </p>
        <Btn onClick={() => setEditing(NEW_PROMO)} testid="promo-new">NEW PROMOTION</Btn>
      </div>

      {editing && (
        <Card className="mb-6 grid md:grid-cols-4 gap-4">
          <Sel label="KIND" value={editing.kind} onChange={(v) => set("kind", v)}
            options={[{ v: "CODE", l: "PROMO CODE (TYPED)" }, { v: "AUTOMATIC", l: "AUTOMATIC (NO CODE)" }]} testid="promo-kind" />
          {editing.kind === "CODE" && (
            <TInput label="CODE" value={editing.code} onChange={(v) => set("code", v.toUpperCase())} placeholder="DIMENSION10" testid="promo-code" />
          )}
          <TInput label="INTERNAL CAMPAIGN NAME" value={editing.campaignName} onChange={(v) => set("campaignName", v)} testid="promo-campaign" />
          <Sel label="DISCOUNT TYPE" value={editing.discountType} onChange={(v) => set("discountType", v)}
            options={[{ v: "PERCENT", l: "PERCENTAGE OFF" }, { v: "FIXED", l: "FIXED AMOUNT OFF" }, { v: "FREE_SHIPPING", l: "FREE SHIPPING" }]} testid="promo-type" />
          {editing.discountType !== "FREE_SHIPPING" && (
            <TInput label={editing.discountType === "PERCENT" ? "PERCENT (1–100)" : "AMOUNT OFF (CENTS)"}
              value={editing.value} onChange={(v) => set("value", Number(v) || 0)} type="number" testid="promo-value" />
          )}
          <TInput label="STARTS AT" value={editing.startsAt} onChange={(v) => set("startsAt", v || "")} type="datetime-local" testid="promo-starts" />
          <TInput label="EXPIRES AT" value={editing.endsAt} onChange={(v) => set("endsAt", v || "")} type="datetime-local" testid="promo-ends" />
          <TInput label="MIN PURCHASE (CENTS, OPTIONAL)" value={editing.minPurchase} onChange={(v) => set("minPurchase", v === "" ? null : Number(v))} type="number" testid="promo-min" />
          <TInput label="MAX TOTAL USES (OPTIONAL)" value={editing.maxTotalUses} onChange={(v) => set("maxTotalUses", v === "" ? null : Number(v))} type="number" />
          <TInput label="MAX USES PER CUSTOMER (OPTIONAL)" value={editing.maxUsesPerCustomer} onChange={(v) => set("maxUsesPerCustomer", v === "" ? null : Number(v))} type="number" />
          <TInput label="ELIGIBLE EMAILS (COMMA, OPTIONAL)" value={(editing.eligibleEmails || []).join(",")}
            onChange={(v) => set("eligibleEmails", v.split(",").map((s) => s.trim()).filter(Boolean))} />
          <TInput label="INTERNAL NOTES" value={editing.internalNotes} onChange={(v) => set("internalNotes", v)} />

          <ProductMultiPicker label="APPLICABLE PRODUCTS (EMPTY = ALL)" value={editing.applicableProductIds || []}
            onChange={(v) => set("applicableProductIds", v)} field="id" testid="promo-applicable-products" />
          <CategoryToggles label="APPLICABLE CATEGORIES (EMPTY = ALL)" value={editing.applicableCategoryIds || []}
            onChange={(v) => set("applicableCategoryIds", v)} categories={categories} testid="promo-applicable-cats" />
          <ProductMultiPicker label="EXCLUDED PRODUCTS" value={editing.excludedProductIds || []}
            onChange={(v) => set("excludedProductIds", v)} field="id" testid="promo-excluded-products" />
          <CategoryToggles label="EXCLUDED CATEGORIES" value={editing.excludedCategoryIds || []}
            onChange={(v) => set("excludedCategoryIds", v)} categories={categories} testid="promo-excluded-cats" />

          <div className="md:col-span-4 flex flex-wrap gap-6 border-t border-white/10 pt-4">
            <Toggle label="ACTIVE" checked={!!editing.active} onChange={(v) => set("active", v)} testid="promo-active" />
            <Toggle label="NEW CUSTOMERS ONLY" checked={!!editing.newCustomersOnly} onChange={(v) => set("newCustomersOnly", v)} testid="promo-newonly" />
            <Toggle label="STACKABLE (DEFAULT OFF)" checked={!!editing.stackable} onChange={(v) => set("stackable", v)} testid="promo-stackable" />
          </div>
          <div className="flex gap-3 md:col-span-4">
            <Btn onClick={save} testid="promo-save">SAVE</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)}>CANCEL</Btn>
          </div>
        </Card>
      )}

      {stats && (
        <Card className="mb-6" data-testid="promo-stats">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[10px] tracking-[0.3em] text-gold">
              CAMPAIGN — {stats.promotion.campaignName || stats.promotion.code}
            </p>
            <button onClick={() => setStats(null)} className="text-white/40 hover:text-white text-sm">✕</button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">REDEMPTIONS</p>
              <p className="font-serif text-3xl text-white mt-1">{stats.redemptions}</p></div>
            <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">TOTAL DISCOUNT</p>
              <p className="font-serif text-3xl text-gold mt-1">{money(stats.totalDiscount)}</p></div>
            <div><p className="font-mono text-[8px] tracking-[0.25em] text-white/40">STATUS</p>
              <p className="font-serif text-3xl text-white mt-1">{stats.promotion.active ? "LIVE" : "OFF"}</p></div>
          </div>
          {stats.orders.map((r) => (
            <p key={r.id} className="font-mono text-[9px] text-white/50 border-t border-white/10 py-2">
              {r.orderNumber} · {r.email || "guest"} · −{money(r.discountAmount)} · {String(r.at).slice(0, 10)}
            </p>
          ))}
        </Card>
      )}

      {items.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-4 border-t border-white/10 py-4 px-2" data-testid={`promo-row-${p.id}`}>
          <div className="min-w-0">
            <p className="text-white/90 font-mono text-sm">{p.kind === "CODE" ? p.code : "AUTOMATIC"}
              <span className="text-white/40 text-xs ml-3">{p.campaignName}</span></p>
            <p className="font-mono text-[9px] text-white/40 mt-1">
              {p.discountType === "PERCENT" ? `${p.value}% OFF` : p.discountType === "FIXED" ? `${money(p.value)} OFF` : "FREE SHIPPING"}
              {" · "}{p.usesCount || 0} USES{p.maxTotalUses ? ` / ${p.maxTotalUses}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {p.active ? <Badge tone="green">ACTIVE</Badge> : <Badge tone="dim">INACTIVE</Badge>}
            <Btn variant="ghost" onClick={() => showStats(p)} testid={`promo-stats-${p.id}`}>STATS</Btn>
            <Btn variant="ghost" onClick={() => setEditing(p)} testid={`promo-edit-${p.id}`}>EDIT</Btn>
            <button onClick={() => destroy(p)} className="text-red-400/70 hover:text-red-400 text-xs px-1">✕</button>
          </div>
        </div>
      ))}
      {items.length === 0 && !editing && <p className="text-white/40 text-sm">No promotions yet.</p>}
    </div>
  );
}
